interface NavigatorUAData {
  platform?: string;
  brands?: Array<{ brand: string; version: string }>;
  mobile?: boolean;
}

declare global {
  interface Navigator {
    userAgentData?: NavigatorUAData;
  }
}

export interface BrowserInfo {
  name: string;
  version: string;
  majorVersion: number;
  isSmartTV: boolean;
  isLegacyTV: boolean;
  platform: string;
  supportsIMA: boolean;
  supportsModernJS: boolean;
  recommendedAdPlayer: 'ima' | 'hls';
}

function getChromeVersion(ua: string): number {
  const match = ua.match(/Chrome\/(\d+)/);
  return match && match[1] ? parseInt(match[1], 10) : 0;
}

function getWebKitVersion(ua: string): number {
  const match = ua.match(/AppleWebKit\/(\d+)/);
  return match && match[1] ? parseInt(match[1], 10) : 0;
}

function getPlatform(): string {
  if ('userAgentData' in navigator && navigator.userAgentData?.platform) {
    return navigator.userAgentData.platform;
  }

  const ua = navigator.userAgent;
  if (/Mac|iPhone|iPad|iPod/i.test(ua)) {
    return /iPhone|iPad|iPod/i.test(ua) ? 'iPhone' : 'MacIntel';
  }
  if (/Win/i.test(ua)) {
    return 'Win32';
  }
  if (/Linux/i.test(ua)) {
    return /Android/i.test(ua) ? 'Linux armv8l' : 'Linux x86_64';
  }
  if (/CrOS/i.test(ua)) {
    return 'CrOS';
  }

  // eslint-disable-next-line deprecation/deprecation
  return (navigator as any).platform || 'Unknown';
}

export function detectBrowser(): BrowserInfo {
  const ua = navigator.userAgent;
  const platform = getPlatform();

  let name = 'Unknown';
  let version = '0';
  let majorVersion = 0;
  let isSmartTV = false;
  let isLegacyTV = false;
  let supportsIMA = true;
  let supportsModernJS = true;
  let recommendedAdPlayer: 'ima' | 'hls' = 'ima';

  if (/Web0S|webOS/i.test(ua)) {
    name = 'LG WebOS';
    isSmartTV = true;
    const match = ua.match(/Web0S[/\s]*([\d.]+)/i);
    version = match && match[1] ? match[1] : 'Unknown';
    if (version !== 'Unknown') {
      const parts = version.split('.');
      majorVersion = parts[0] ? parseInt(parts[0], 10) : 0;
    }
  } else if (/Tizen/i.test(ua)) {
    name = 'Samsung Tizen';
    isSmartTV = true;
    const match = ua.match(/Tizen[/\s]*([\d.]+)/i);
    version = match && match[1] ? match[1] : 'Unknown';
    if (version !== 'Unknown') {
      const parts = version.split('.');
      majorVersion = parts[0] ? parseInt(parts[0], 10) : 0;
    }
  } else if (/SMART-TV|SmartTV/i.test(ua)) {
    name = 'Smart TV';
    isSmartTV = true;
  } else if (/NetCast/i.test(ua)) {
    name = 'LG NetCast';
    isSmartTV = true;
    isLegacyTV = true;
  } else if (/BRAVIA/i.test(ua)) {
    name = 'Sony BRAVIA';
    isSmartTV = true;
  }

  const chromeVersion = getChromeVersion(ua);
  const webkitVersion = getWebKitVersion(ua);

  if (chromeVersion > 0) {
    if (!isSmartTV) {
      name = 'Chrome';
      version = chromeVersion.toString();
      majorVersion = chromeVersion;
    }

    if (chromeVersion < 50) {
      supportsIMA = false;
      supportsModernJS = false;
      isLegacyTV = true;
      recommendedAdPlayer = 'hls';
    }
  }

  if (webkitVersion > 0 && webkitVersion < 600) {
    supportsModernJS = false;
    if (isSmartTV) {
      isLegacyTV = true;
      supportsIMA = false;
      recommendedAdPlayer = 'hls';
    }
  }

  if (typeof Promise === 'undefined' ||
      typeof Map === 'undefined' ||
      typeof Set === 'undefined') {
    supportsModernJS = false;
    supportsIMA = false;
    recommendedAdPlayer = 'hls';
  }

  if (typeof URLSearchParams === 'undefined') {
    supportsModernJS = false;
  }

  return {
    name,
    version,
    majorVersion,
    isSmartTV,
    isLegacyTV,
    platform,
    supportsIMA,
    supportsModernJS,
    recommendedAdPlayer,
  };
}

export function supportsGoogleIMA(): boolean {
  const browser = detectBrowser();

  if (browser.isLegacyTV) {
    return false;
  }

  if (typeof document === 'undefined' ||
      typeof document.createElement !== 'function') {
    return false;
  }

  try {
    const video = document.createElement('video');
    if (!video) {
      return false;
    }
  } catch (e) {
    return false;
  }

  if (typeof Promise === 'undefined') {
    return false;
  }

  return browser.supportsIMA;
}

export function getRecommendedAdPlayer(): 'ima' | 'hls' {
  const browser = detectBrowser();
  return browser.recommendedAdPlayer;
}

export function supportsModernJS(): boolean {
  try {
    return (
      typeof Promise !== 'undefined' &&
      typeof Map !== 'undefined' &&
      typeof Set !== 'undefined' &&
      typeof Array.from !== 'undefined' &&
      typeof Object.assign !== 'undefined' &&
      typeof Array.prototype.forEach !== 'undefined' &&
      typeof String.prototype.includes !== 'undefined'
    );
  } catch (e) {
    return false;
  }
}

export function logBrowserInfo(debug: boolean = false): void {
  if (!debug) return;

  const browser = detectBrowser();
  const imaSupport = supportsGoogleIMA();

  console.log('[StormcloudVideoPlayer] Browser Compatibility Info:', {
    browser: `${browser.name} ${browser.version}`,
    platform: browser.platform,
    isSmartTV: browser.isSmartTV,
    isLegacyTV: browser.isLegacyTV,
    supportsIMA: imaSupport,
    supportsModernJS: browser.supportsModernJS,
    recommendedAdPlayer: browser.recommendedAdPlayer,
    userAgent: navigator.userAgent,
  });
}

export function getBrowserConfigOverrides(): {
  adPlayerType?: 'ima' | 'hls';
  allowNativeHls?: boolean;
} {
  const browser = detectBrowser();
  const overrides: any = {};

  if (browser.isLegacyTV || !browser.supportsIMA) {
    overrides.adPlayerType = 'hls';
  }

  if (browser.isSmartTV) {
    overrides.allowNativeHls = true;
  }

  return overrides;
}

export function supportsFeature(feature: string): boolean {
  switch (feature) {
    case 'ima':
      return supportsGoogleIMA();
    case 'urlsearchparams':
      return typeof URLSearchParams !== 'undefined';
    case 'textencoder':
      return typeof TextEncoder !== 'undefined';
    case 'promises':
      return typeof Promise !== 'undefined';
    case 'fetch':
      return typeof fetch !== 'undefined';
    case 'crypto':
      return typeof crypto !== 'undefined' && typeof crypto.subtle !== 'undefined';
    default:
      return false;
  }
}

